import {
  Box,
  Button, Flex, Text
} from '@huspy/briks-web';
import { useTranslation } from 'react-i18next';
import { BaseSlido } from '@modules/core/components/BaseSlido';

import { ProposalEntity } from '@modules/clients/entities/client/api/dto/clientProposal.dto';
import { convertToDDMMYYYYFormat, timeUntilHHMM } from '@shared/utils';
import { ProposalTemplate } from '@huspy/forge/templates';
import useGetProposalPreviewById from '@modules/proposals/api/query/useGetProposalPreviewById';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import { rem, Stack } from '@mantine/core';
import {
  ChevronsLeftRight, ChevronsRightLeft, ZoomIn, ZoomOut
} from '@huspy/briks-icons';
import briksTheme from '@shared/briks-theme';
import { css } from '@huspy/styled-system/css';
import { AnimatedLoader } from '@components/AnimatedLoader';
import CreditsCharge from '@components/CreditsCharge';
import { trackAmplitudeEvent } from '@shared/analytics/amplitude';
import { USER_EVENTS } from '@shared/analytics/events';
import useRequestDigitalProposalPdf from '@modules/proposals/api/mutation/useRequestDigitalProposalPdf';
import { ProposalShareSlido } from '@modules/proposals/presentation/v2/pages/ProposalCreation/views/Configurator/components/ProposalShareSlido';
import { useDisclosure } from '@mantine/hooks';
import { useNavigate } from '@tanstack/react-router';
import {
  useCallback, useEffect, useState
} from 'react';
import { PROPOSAL_CREATION_RULES } from '@modules/proposals/presentation/v2/pages/ProposalCreation/rules';
import useDuplicateProposal from '@modules/proposals/api/mutation/useDuplicateProposal';
import { ProposalDomain } from '@modules/proposals/domain/proposal';
import useCredits from '@modules/core/hooks/useCredits';
import { ProposalCreationClientAndMortgageRoute, ProposalsListRoute } from '@modules/proposals/presentation/routes';
import { initProposalDownload } from '@modules/proposals/presentation/utils';
import { CREDITS_CHARGE_ACTION_TYPES } from '@modules/core/api/const';

const boxStyles = css({
  borderRadius: '24px',
  overflow: 'hidden',
  boxShadow: briksTheme.shadow['shadow-2'],
  border: '1px solid rgba(24, 26, 24, 0.01)',
  backgroundColor: 'base.white',
});

type ProposalPreviewSlidoProps = {
  proposal: ProposalEntity;
  isOpen: boolean;
  isInitiallyExpanded?: boolean;
  isExpandable?: boolean;
  hideFooter?: boolean;
  open: () => void;
  close: () => void;
};

export const ProposalPreviewSlido = ({
  proposal, isOpen, isInitiallyExpanded = false, isExpandable = true, hideFooter = false, open, close,
}: ProposalPreviewSlidoProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate({ from: ProposalsListRoute.fullPath });
  const [isShareSlidoOpen, { open: openShareSlido, close: closeShareSlido }] = useDisclosure(false);
  const { data, isLoading } = useGetProposalPreviewById(proposal.external_id);
  const { mutateAsync: downloadProposalPDF, isPending: isDownloadProposalPDFPending } = useRequestDigitalProposalPdf();
  const {
    mutateAsync: duplicateProposal,
    isPending: isDuplicateProposalPending,
  } = useDuplicateProposal();

  const { callCreditsAction } = useCredits();

  const [isExpanded, setIsExpanded] = useState(isInitiallyExpanded);

  const canDuplicate = ProposalDomain.canDuplicate(proposal);
  const canEdit = ProposalDomain.canEdit(proposal);
  const fileName = `${t('proposals.fileNamePrefix')} - ${proposal.first_name} ${proposal.last_name}.pdf`;

  const onDuplicateProposal = useCallback(async () => {
    callCreditsAction(
      CREDITS_CHARGE_ACTION_TYPES.proposalGenerate,
      async () => {
        const newProposal = await duplicateProposal({ external_id: proposal.external_id });
        navigate({ to: ProposalCreationClientAndMortgageRoute.to, params: { id: newProposal.external_id } });
      },
      (error: Error) => {
        trackAmplitudeEvent(USER_EVENTS.PROPOSAL.PROPOSAL_DUPLICATION_FAILED, {
          useContext: true,
          blocked: 1,
          proposal_id: proposal.external_id,
          error_message: error.message,
        });
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duplicateProposal, navigate, proposal.external_id]);

  const handleDownloadProposal = useCallback(async () => {
    await initProposalDownload(
      fileName,
      'proposal-template',
      downloadProposalPDF
    );
    trackAmplitudeEvent(USER_EVENTS.PROPOSAL.DOWNLOADED);
  }, [downloadProposalPDF, fileName]);

  const handleEditProposal = useCallback(() => {
    navigate({ to: PROPOSAL_CREATION_RULES.getCreateProposalLatestRoute(proposal), params: { id: proposal.external_id } });
  }, [navigate, proposal]);

  useEffect(() => {
    if (!isOpen && !isInitiallyExpanded) {
      setIsExpanded(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <>
      <ProposalShareSlido
        close={ closeShareSlido }
        onDone={ () => {
          closeShareSlido();
          open();
        } }
        doneText={ t('proposals.proposalsPreview.goBack') }
        open={ openShareSlido }
        opened={ isShareSlidoOpen }
        proposal={ proposal }
      />

      <BaseSlido
        opened={ isOpen }
        close={ close }
        size={ isExpanded ? '100%' : 'xl' }
        title={ (
          <Flex direction='row' p='6' pb='0' justify='space-between' gap={ 4 } align='center'>
            <Flex direction='column' gap='2'>
              <Text size='5xl'>
                {t('proposals.proposalsPreview.title')}
                {' '}
                -
                {' '}
                {proposal.first_name}
                {' '}
                {proposal.last_name}
              </Text>

              <Text size='md' color='neutral.500'>
                {t('proposals.proposalsPreview.lastModified')}
                :
                {' '}
                {convertToDDMMYYYYFormat(proposal.updated_at)}
              </Text>
            </Flex>
          </Flex>
        ) }
        footer={ hideFooter ? null : (
          <Flex justifyContent='space-between'>
            <Flex gap='2'>
              {canDuplicate && (
                <Button
                  type='button'
                  onClick={ onDuplicateProposal }
                  data-test='discard-btn'
                  data-testid='proposals.proposalsPreview.actions.duplicate'
                  disabled={ isDuplicateProposalPending }
                  loading={ isDuplicateProposalPending }
                  variant={ isDuplicateProposalPending ? 'outline' : 'secondary' }
                >
                  <Flex gap={ 2 } align='center'>
                    <Text>{t('proposals.proposalsPreview.actions.duplicate')}</Text>
                    <Text size='xs' color='text-secondary'>|</Text>
                    <CreditsCharge
                      action_type={ CREDITS_CHARGE_ACTION_TYPES.proposalGenerate }
                      color='text-primary'
                      showLeftLine={ false }
                    />
                  </Flex>
                </Button>
              )}

              <Button
                type='button'
                onClick={ handleEditProposal }
                variant='secondary'
                data-test='discard-btn'
                data-testid='proposals.proposalsPreview.actions.edit'
                disabled={ !canEdit }
              >
                <Flex gap={ 2 } align='center'>
                  <Text>{t('proposals.proposalsPreview.actions.edit')}</Text>
                  {proposal.editable_until && (
                    <>
                      <Text size='xs' color='text-secondary'>|</Text>
                      <Text color='text-secondary' weight='medium'>
                        {t('proposals.proposalsPreview.actions.availableFor')}
                        {' '}
                        {timeUntilHHMM(proposal.editable_until)}
                      </Text>
                    </>
                  )}
                </Flex>
              </Button>
            </Flex>

            <Flex gap='2'>
              <Button
                type='button'
                data-test='discard-btn'
                disabled={ isDownloadProposalPDFPending }
                loading={ isDownloadProposalPDFPending }
                variant={ isDownloadProposalPDFPending ? 'outline' : 'primary' }
                onClick={ handleDownloadProposal }
              >
                {t('proposals.proposalsPreview.actions.download')}
              </Button>

              <Button
                type='button'
                onClick={ () => {
                  openShareSlido();
                  close();
                } }
                data-test='discard-btn'
              >
                {t('proposals.proposalsPreview.actions.share')}
              </Button>
            </Flex>
          </Flex>
        ) }
      >
        <Flex bg='utility-blue-100' justify='center' h={ isLoading ? '100%' : 'auto' }>
          {isLoading ? (
            <Flex justify='center' alignItems='center'>
              <AnimatedLoader dark />
            </Flex>
          ) : (
            <TransformWrapper
              initialScale={ isExpanded ? 1 : 0.46 }
              maxScale={ 4 }
              minScale={ 0.35 }
              centerZoomedOut
              wheel={ { disabled: true } }
              initialPositionY={ 30 }
              initialPositionX={ isExpanded ? (document.body.clientWidth - 1300) / 2 : 90 }
            >
              {({ zoomIn, zoomOut, resetTransform }) => (
                <>
                  <Stack
                    gap={ briksTheme.spacing['0,5'] }
                    pos='absolute'
                    right={ 10 }
                    top={ 10 }
                    style={ { zIndex: 99 } }
                  >
                    <Button type='button' opacity={ 0.7 } onClick={ () => zoomIn(0.3) } size='sm' variant='secondary'>
                      <ZoomIn />
                    </Button>
                    <Button type='button' opacity={ 0.7 } onClick={ () => zoomOut(0.3) } size='sm' variant='secondary'>
                      <ZoomOut />
                    </Button>
                    <Button opacity={ 0.7 } type='button' onClick={ () => resetTransform() } size='sm' variant='secondary'>
                      Reset
                    </Button>
                    {isExpandable ? (
                      <Button
                        opacity={ 0.7 }
                        type='button'
                        onClick={ () => {
                          setIsExpanded((prev) => !prev);
                          setTimeout(() => {
                            resetTransform();
                          }, 0);
                        } }
                        size='sm'
                        variant='secondary'
                      >
                        {isExpanded
                          ? <ChevronsRightLeft style={ { transform: 'rotate(45deg)' } } />
                          : <ChevronsLeftRight style={ { transform: 'rotate(45deg)' } } />}
                      </Button>
                    ) : null}
                  </Stack>
                  <TransformComponent
                    wrapperStyle={ {
                      maxWidth: '100%',
                      right: 0,
                      width: isExpanded ? '100%' : rem(800),
                      maxHeight: isExpanded ? rem(3300) : rem(1300),
                    } }
                  >
                    <Box bg='white' borderRadius={ 12 } className={ boxStyles }>
                      <ProposalTemplate
                        { ...data! }
                      />
                    </Box>
                  </TransformComponent>
                </>
              )}
            </TransformWrapper>
          )}
        </Flex>
      </BaseSlido>
    </>
  );
};
